import { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpRight, ArrowDownRight, TrendingUp, TrendingDown, DollarSign, Clock, ShieldAlert, MoreVertical, X, Check } from 'lucide-react';
import './trading.css';

function formatDuration(sec) {
  if (sec < 60) return `00:${sec.toString().padStart(2, '0')}`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export function EZ5050OrderPanel({
  balance = 0,
  payoutRate = 0.82,
  onExecuteOrder,
  isDisabled = false,
  timeSettings = [],
  amountSettings = [],
  luckyData,
  wallet,
}) {
  const [amountInput, setAmountInput] = useState('10');
  const [duration, setDuration] = useState(30);
  const [isTimeMenuOpen, setIsTimeMenuOpen] = useState(false);
  const [isTimeMenuClosing, setIsTimeMenuClosing] = useState(false);
  const [isAmountMenuOpen, setIsAmountMenuOpen] = useState(false);
  const [isAmountMenuClosing, setIsAmountMenuClosing] = useState(false);
  const [isOptimisticallyConsumed, setIsOptimisticallyConsumed] = useState(false);

  const timeMenuTimerRef = useRef(null);
  const amountMenuTimerRef = useRef(null);

  const openTimeMenu = () => {
    if (timeMenuTimerRef.current) clearTimeout(timeMenuTimerRef.current);
    setIsTimeMenuClosing(false);
    setIsTimeMenuOpen(true);
  };

  const closeTimeMenu = (callback) => {
    if (isTimeMenuClosing) return;
    setIsTimeMenuClosing(true);
    if (timeMenuTimerRef.current) clearTimeout(timeMenuTimerRef.current);
    timeMenuTimerRef.current = setTimeout(() => {
      setIsTimeMenuOpen(false);
      setIsTimeMenuClosing(false);
      timeMenuTimerRef.current = null;
      callback && callback();
    }, 220);
  };

  const openAmountMenu = () => {
    if (amountMenuTimerRef.current) clearTimeout(amountMenuTimerRef.current);
    setIsAmountMenuClosing(false);
    setIsAmountMenuOpen(true);
  };

  const closeAmountMenu = (callback) => {
    if (isAmountMenuClosing) return;
    setIsAmountMenuClosing(true);
    if (amountMenuTimerRef.current) clearTimeout(amountMenuTimerRef.current);
    amountMenuTimerRef.current = setTimeout(() => {
      setIsAmountMenuOpen(false);
      setIsAmountMenuClosing(false);
      amountMenuTimerRef.current = null;
      callback && callback();
    }, 220);
  };

  useEffect(() => {
    return () => {
      if (timeMenuTimerRef.current) clearTimeout(timeMenuTimerRef.current);
      if (amountMenuTimerRef.current) clearTimeout(amountMenuTimerRef.current);
    };
  }, []);

  const parsedAmount = Math.max(0, parseFloat(amountInput) || 0);
  const payoutAmount = parsedAmount + parsedAmount * payoutRate;
  const roiPercentage = (payoutRate * 100).toFixed(2);

  const serverMultiplier = useMemo(() => {
    // 1. Check luckyData (from /users/getLuckyRoundDetails)
    if (luckyData?.isLucky && Number(luckyData?.multiplier) > 1) {
      return Number(luckyData.multiplier);
    }
    if (Number(luckyData?.multiplier) > 1) {
      return Number(luckyData.multiplier);
    }
    // 2. Check wallet activeMultiplier (e.g. from spin reward)
    if (Number(wallet?.binary?.activeMultiplier) > 1) {
      return Number(wallet.binary.activeMultiplier);
    }
    return 1;
  }, [luckyData, wallet]);

  const activeMultiplier = useMemo(() => {
    if (isOptimisticallyConsumed) return 1;
    return serverMultiplier;
  }, [isOptimisticallyConsumed, serverMultiplier]);

  const hasActiveMultiplier = activeMultiplier > 1;

  const multiplierLabel = useMemo(() => {
    if (!hasActiveMultiplier) return '';
    return activeMultiplier % 1 === 0
      ? `${activeMultiplier.toFixed(0)}x`
      : `${parseFloat(activeMultiplier.toFixed(4))}x`;
  }, [hasActiveMultiplier, activeMultiplier]);

  // When serverMultiplier returns to 1, reset optimistic consumption
  useEffect(() => {
    if (serverMultiplier <= 1 && isOptimisticallyConsumed) {
      setIsOptimisticallyConsumed(false);
    }
  }, [serverMultiplier, isOptimisticallyConsumed]);

  // Also listen for clear-lucky-multiplier event if triggered elsewhere
  useEffect(() => {
    const handleClear = () => {
      setIsOptimisticallyConsumed(true);
    };
    window.addEventListener('clear-lucky-multiplier', handleClear);
    return () => window.removeEventListener('clear-lucky-multiplier', handleClear);
  }, []);

  const handlePercentageSelect = (pct) => {
    const calculated = (balance * pct).toFixed(2);
    setAmountInput(calculated);
  };

  const handlePresetSelect = (val) => {
    setAmountInput(val.toString());
  };

  const handleExecute = (direction) => {
    if (parsedAmount <= 0 || isDisabled) return;
    if (parsedAmount > balance) {
      alert('Insufficient account balance!');
      return;
    }
    if (hasActiveMultiplier) {
      setIsOptimisticallyConsumed(true);
    }
    onExecuteOrder && onExecuteOrder(direction, parsedAmount, duration);
  };

  return (
    <div className="h-full w-full select-none" style={{ width: '100%' }}>
      {/* --- DESKTOP ORDER PANEL (hidden on mobile, visible on md+) --- */}
      <div className="hidden md:flex h-full w-full p-4 overflow-y-auto ez-trading-scroll flex-col justify-between ez-order-panel-desktop">
        <div className="space-y-4">
          {/* Panel Header */}
          <div className="flex items-center justify-between pb-3 border-b border-white/[0.08]">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-white text-[15px] tracking-tight">Order Execution</h2>
              {hasActiveMultiplier && (
                <div className="ez-lucky-badge" title="Active Round Multiplier">
                  <span className="ez-lucky-value">{multiplierLabel}</span>
                  <span className="ez-lucky-tag">Now</span>
                </div>
              )}
            </div>
            <div className="text-[11px] text-slate-400 font-medium">Min $1.00 • Max $5,000</div>
          </div>

          {/* Amount Input */}
          <div className="space-y-2">
            <div className="flex justify-between items-center text-xs font-medium">
              <span className="text-slate-300">Amount</span>
              <span className="text-slate-400">
                Bal: <span className="text-white font-semibold">${balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </span>
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <DollarSign className="w-4 h-4 text-[#fc4420]" />
              </div>
              <input
                type="number"
                min={1}
                step="any"
                value={amountInput}
                onChange={(e) => setAmountInput(e.target.value)}
                placeholder="0.00"
                className="ez-order-input w-full rounded-xl py-2.5 pl-8 pr-3 text-base font-semibold text-white focus:outline-none"
              />
            </div>

            {/* Percentage buttons */}
            <div className="grid grid-cols-4 gap-1.5 pt-1">
              {[
                { label: '25%', pct: 0.25 },
                { label: '50%', pct: 0.5 },
                { label: '100%', pct: 1.0 },
              ].map((btn) => (
                <button
                  key={btn.label}
                  onClick={() => handlePercentageSelect(btn.pct)}
                  className="ez-pct-btn py-1.5 rounded-lg text-xs font-semibold transition-all"
                >
                  {btn.label}
                </button>
              ))}
              <button
                onClick={() => handlePercentageSelect(1.0)}
                className="ez-pct-btn is-max py-1.5 rounded-lg text-xs font-semibold transition-all"
              >
                Max
              </button>
            </div>

            {/* Cash presets */}
            <div className="flex space-x-1.5">
              {[10, 50, 100, 250].map((val) => (
                <button
                  key={val}
                  onClick={() => handlePresetSelect(val)}
                  className={`ez-preset-btn flex-1 py-1 rounded-md text-[11px] font-semibold transition-all ${
                    parsedAmount === val ? 'is-active' : ''
                  }`}
                >
                  ${val}
                </button>
              ))}
            </div>
          </div>

          {/* Duration Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs font-medium">
              <span className="flex items-center space-x-1.5 text-slate-300">
                <Clock className="w-3.5 h-3.5 text-[#fc4420]" />
                <span>Duration</span>
              </span>
              <span className="text-[11px] text-slate-400 font-medium">Contract Time</span>
            </div>

            <div className="grid grid-cols-4 gap-1.5">
              {[
                { label: '10s', sec: 10 },
                { label: '30s', sec: 30 },
                { label: '1m', sec: 60 },
                { label: '5m', sec: 300 },
              ].map((d) => (
                <button
                  key={d.sec}
                  onClick={() => setDuration(d.sec)}
                  className={`ez-duration-btn py-2 rounded-xl text-xs font-semibold transition-all ${
                    duration === d.sec ? 'is-active' : ''
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          {/* Payout & ROI */}
          <div className="ez-payout-card rounded-xl p-3.5 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium text-slate-400">Est. Payout</span>
              <span className="text-lg font-bold text-[#00c076] tracking-tight">
                ${payoutAmount.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center text-xs pt-2 border-t border-white/[0.06]">
              <span className="text-slate-400 font-medium">Return on Investment (ROI)</span>
              <span className="font-bold text-[#00c076]">+{roiPercentage}%</span>
            </div>
          </div>
        </div>

        {/* UP / DOWN Buttons */}
        <div className="pt-3 mt-3 border-t border-white/[0.08]">
          <div className="flex items-center space-x-2.5">
            <button
              onClick={() => handleExecute('UP')}
              disabled={isDisabled}
              className={`ez-btn-up flex-1 py-3.5 rounded-xl text-white font-semibold text-sm tracking-wide transition-all flex items-center justify-center space-x-1.5 cursor-pointer transform active:scale-95 whitespace-nowrap flex-nowrap ${hasActiveMultiplier ? 'ez-btn-beat' : ''}`}
              style={{ opacity: isDisabled ? 0.5 : 1 }}
            >
              <TrendingUp className="w-4 h-4 shrink-0" style={{ strokeWidth: 2.5 }} />
              <span>{hasActiveMultiplier ? `Up ${multiplierLabel}` : 'Up'}</span>
            </button>

            <button
              onClick={() => handleExecute('DOWN')}
              disabled={isDisabled}
              className={`ez-btn-down flex-1 py-3.5 rounded-xl text-white font-semibold text-sm tracking-wide transition-all flex items-center justify-center space-x-1.5 cursor-pointer transform active:scale-95 whitespace-nowrap flex-nowrap ${hasActiveMultiplier ? 'ez-btn-beat' : ''}`}
              style={{ opacity: isDisabled ? 0.5 : 1 }}
            >
              <TrendingDown className="w-4 h-4 shrink-0" style={{ strokeWidth: 2.5 }} />
              <span>{hasActiveMultiplier ? `Down ${multiplierLabel}` : 'Down'}</span>
            </button>
          </div>

          <div className="flex items-center justify-center space-x-1 text-[10px] text-center pt-2 text-slate-500 font-medium">
            <ShieldAlert className="w-3.5 h-3.5 text-[#fc4420]/70" />
            <span>Instant Settlement • Fair Market Oracle</span>
          </div>
        </div>
      </div>

      {/* --- MOBILE TRADING DOCK (visible on mobile only, hidden on md+) --- */}
      <div className="flex md:hidden flex-col p-2.5 space-y-2 rounded-t-2xl shadow-2xl ez-mobile-dock">
        {/* Framed Container for Inputs */}
        <div className="flex flex-col space-y-2.5 p-2.5 ez-mobile-frame">
           {/* Row 1: Lucky Round & Jackpot */}
          <div className="flex items-center justify-between text-xs px-1 mb-1 gap-1.5 flex-wrap">
            <div className="font-semibold text-slate-200 flex items-center gap-1.5 shrink-0">
              <span>Lucky Round:</span>
              {hasActiveMultiplier ? (
                <div className="ez-lucky-badge" title="Active Round Multiplier">
                  <span className="ez-lucky-value">{multiplierLabel}</span>
                  <span className="ez-lucky-tag">Now</span>
                </div>
              ) : (
                <span className="text-slate-500 font-medium">-</span>
              )}
            </div>
            <div className="font-semibold text-slate-200 flex items-center gap-1 shrink-0">
              <span className="text-slate-400">Jackpot:</span>
              <span className="bg-[linear-gradient(180deg,#FFE600_26%,#FFF9BE_79%)] bg-clip-text text-transparent font-bold text-xs sm:text-sm">
                ${(Number(wallet?.spin_and_win?.pool_amount || 0)).toLocaleString()}
              </span>
            </div>
          </div>

          {/* Row 2: Time Selector & Presets */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center space-x-1.5 shrink-0">
              <span className="text-xs font-medium text-slate-300 w-11 flex items-center gap-1">
                <Clock size={12} className="text-[#fc4420]" />
                Time
              </span>
              <button
                onClick={openTimeMenu}
                className="ez-3d-trigger flex items-center gap-1.5 px-2.5 py-1 min-w-[68px] justify-between"
                title="Select contract duration"
              >
                <span className="text-xs font-semibold text-white tracking-wide">{formatDuration(duration)}</span>
                <MoreVertical size={13} className="text-slate-400 shrink-0" />
              </button>
            </div>
            <div className="flex items-center gap-1.5 flex-1 justify-end">
              {(() => {
                let finalOptions = [];
                if (timeSettings && timeSettings.length > 0) {
                  finalOptions = timeSettings.map(s => ({ label: s.label || `${s.seconds}s`, sec: Number(s.seconds) }));
                  finalOptions.sort((a, b) => a.sec - b.sec);
                } else {
                  finalOptions = [
                    { label: '10s', sec: 10 },
                    { label: '20s', sec: 20 },
                    { label: '30s', sec: 30 },
                    { label: '1m', sec: 60 },
                  ];
                }
                return finalOptions.slice(0, 4);
              })().map((d) => (
                <button
                  key={d.sec}
                  onClick={() => setDuration(d.sec)}
                  className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all border ${
                    duration === d.sec
                      ? 'bg-[#fc4420]/20 text-[#fc4420] border-[#fc4420] shadow-[0_0_8px_rgba(252,68,32,0.3)]'
                      : 'bg-white/[0.04] text-slate-300 border-white/[0.08] hover:border-white/20'
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          {/* Subtle separator line */}
          <div className="w-full border-t border-white/[0.06]" />

          {/* Row 3: Amount Selector & Presets */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col shrink-0">
              <div className="flex items-center space-x-1.5">
                <span className="text-xs font-medium text-slate-300 w-11 flex items-center gap-1">
                  <DollarSign size={12} className="text-[#00c076]" />
                  Amt
                </span>
                <button
                  onClick={openAmountMenu}
                  className="ez-3d-trigger flex items-center gap-1.5 px-2.5 py-1 min-w-[68px] justify-between"
                  title="Select trade amount"
                >
                  <span className="text-xs font-semibold text-white tracking-wide">${parsedAmount.toFixed(parsedAmount % 1 === 0 ? 0 : 2)}</span>
                  <MoreVertical size={13} className="text-slate-400 shrink-0" />
                </button>
              </div>
              <span className="text-[9px] text-slate-400 leading-tight mt-0.5 ml-12">Min $0.10 · Max $20</span>
            </div>
            <div className="flex items-center gap-1.5 flex-1 justify-end">
              {(() => {
                let finalOptions = [];
                if (amountSettings && amountSettings.length > 0) {
                  finalOptions = amountSettings.map(s => Number(s.amount));
                  finalOptions.sort((a, b) => a - b);
                } else {
                  finalOptions = [0.10, 0.25, 0.50];
                }
                return finalOptions.slice(0, 3);
              })().map((val) => (
                <button
                  key={val}
                  onClick={() => setAmountInput(val.toString())}
                  className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all border ${
                    parseFloat(amountInput) === val
                      ? 'bg-[#fc4420]/20 text-[#fc4420] border-[#fc4420] shadow-[0_0_8px_rgba(252,68,32,0.3)]'
                      : 'bg-white/[0.04] text-slate-300 border-white/[0.08] hover:border-white/20'
                  }`}
                >
                  ${val.toFixed(2)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Row 4: Execution Buttons (Up / Down) */}
        <div className="grid grid-cols-2 gap-2 pt-0.5">
          <button
            onClick={() => handleExecute('UP')}
            disabled={isDisabled}
            className={`ez-btn-up w-full py-3 rounded-xl text-white font-semibold text-sm tracking-wide transition-all flex items-center justify-center space-x-1.5 active:scale-95 shadow-md whitespace-nowrap flex-nowrap ${hasActiveMultiplier ? 'ez-btn-beat' : ''}`}
            style={{ opacity: isDisabled ? 0.5 : 1 }}
          >
            <TrendingUp className="w-4 h-4 shrink-0" style={{ strokeWidth: 2.5 }} />
            <span>{hasActiveMultiplier ? `Up ${multiplierLabel}` : 'Up'}</span>
          </button>

          <button
            onClick={() => handleExecute('DOWN')}
            disabled={isDisabled}
            className={`ez-btn-down w-full py-3 rounded-xl text-white font-semibold text-sm tracking-wide transition-all flex items-center justify-center space-x-1.5 active:scale-95 shadow-md whitespace-nowrap flex-nowrap ${hasActiveMultiplier ? 'ez-btn-beat' : ''}`}
            style={{ opacity: isDisabled ? 0.5 : 1 }}
          >
            <TrendingDown className="w-4 h-4 shrink-0" style={{ strokeWidth: 2.5 }} />
            <span>{hasActiveMultiplier ? `Down ${multiplierLabel}` : 'Down'}</span>
          </button>
        </div>
      </div>

      {/* Time Picker Bottom Sheet (Mobile) */}
      {(isTimeMenuOpen || isTimeMenuClosing) && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[99999] flex flex-col justify-end md:hidden">
          <div
            className={`absolute inset-0 ez-3d-backdrop ${isTimeMenuClosing ? 'is-closing' : ''}`}
            onClick={() => closeTimeMenu()}
          />
          <div className={`relative ez-3d-popup-sheet ${isTimeMenuClosing ? 'is-closing' : ''} p-4 shadow-2xl max-h-[75vh] flex flex-col pb-6`}>
            {/* 3D Grab Bar */}
            <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-3 shadow-[0_1px_2px_rgba(0,0,0,0.6)]" />

            <div className="flex items-center justify-between mb-3.5 flex-shrink-0">
              <div className="flex items-center space-x-2.5">
                <div className="w-8 h-8 rounded-xl bg-[#fc4420]/15 border border-[#fc4420]/30 flex items-center justify-center text-[#fc4420] shadow-[0_0_10px_rgba(252,68,32,0.2)]">
                  <Clock size={16} />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm tracking-tight">Contract Duration</h3>
                  <p className="text-[11px] text-slate-400">Select round expiration timeframe</p>
                </div>
              </div>
              <button
                onClick={() => closeTimeMenu()}
                className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors border border-white/10"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2.5 overflow-y-auto overflow-x-hidden pt-1 max-h-[50vh]">
              {(() => {
                let finalOptions = [];
                if (timeSettings && timeSettings.length > 0) {
                  finalOptions = timeSettings.map(s => ({ label: s.label || `${s.seconds}s`, sec: Number(s.seconds) }));
                  finalOptions.sort((a, b) => a.sec - b.sec);
                } else {
                  finalOptions = [
                    { label: '10s', sec: 10 },
                    { label: '20s', sec: 20 },
                    { label: '30s', sec: 30 },
                    { label: '1m', sec: 60 },
                  ];
                }
                return finalOptions;
              })().map((opt, index) => (
                <button
                  key={opt.sec}
                  onClick={() => {
                    setDuration(opt.sec);
                    closeTimeMenu();
                  }}
                  style={{ animationDelay: `${index * 0.03}s` }}
                  className={`ez-3d-option-card py-3 px-3.5 flex items-center justify-between ${
                    duration === opt.sec ? 'is-selected' : ''
                  }`}
                >
                  <div className="flex flex-col items-start text-left">
                    <span className="font-bold text-sm tracking-wide text-white">{opt.label}</span>
                    <span className="text-[10px] text-slate-400">
                      {opt.sec < 60 ? `${opt.sec} Seconds` : `${Math.floor(opt.sec / 60)} Minute${opt.sec > 60 ? 's' : ''}`}
                    </span>
                  </div>
                  {duration === opt.sec ? (
                    <div className="w-5 h-5 rounded-full bg-[#fc4420] flex items-center justify-center text-white shadow-[0_0_8px_rgba(252,68,32,0.6)]">
                      <Check size={12} strokeWidth={3} />
                    </div>
                  ) : (
                    <div className="w-4 h-4 rounded-full border border-white/20" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Amount Picker Bottom Sheet (Mobile) */}
      {(isAmountMenuOpen || isAmountMenuClosing) && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[99999] flex flex-col justify-end md:hidden">
          <div
            className={`absolute inset-0 ez-3d-backdrop ${isAmountMenuClosing ? 'is-closing' : ''}`}
            onClick={() => closeAmountMenu()}
          />
          <div className={`relative ez-3d-popup-sheet ${isAmountMenuClosing ? 'is-closing' : ''} p-4 shadow-2xl max-h-[80vh] flex flex-col pb-6`}>
            {/* 3D Grab Bar */}
            <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-3 shadow-[0_1px_2px_rgba(0,0,0,0.6)]" />

            <div className="flex items-center justify-between mb-3.5 flex-shrink-0">
              <div className="flex items-center space-x-2.5">
                <div className="w-8 h-8 rounded-xl bg-[#00c076]/15 border border-[#00c076]/30 flex items-center justify-center text-[#00c076] shadow-[0_0_10px_rgba(0,192,118,0.2)]">
                  <DollarSign size={16} />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm tracking-tight">Trade Amount</h3>
                  <p className="text-[11px] text-slate-400">
                    Bal: <span className="text-white font-medium">${balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </p>
                </div>
              </div>
              <button
                onClick={() => closeAmountMenu()}
                className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors border border-white/10"
              >
                <X size={16} />
              </button>
            </div>

            {/* Preset Tile Grid */}
            <div className="grid grid-cols-2 gap-2 overflow-y-auto max-h-[44vh] pr-0.5 pt-0.5">
              {(() => {
                let finalOptions = [];
                if (amountSettings && amountSettings.length > 0) {
                  finalOptions = amountSettings.map(s => ({ label: s.label || `$${s.amount}`, val: Number(s.amount) }));
                } else {
                  finalOptions = [
                    { label: '$1', val: 1 },
                    { label: '$2', val: 2 },
                    { label: '$5', val: 5 },
                    { label: '$15', val: 15 },
                  ];
                }
                return finalOptions.sort((a, b) => a.val - b.val);
              })().map((opt, index) => (
                <button
                  key={opt.val}
                  onClick={() => {
                    setAmountInput(opt.val.toString());
                    closeAmountMenu();
                  }}
                  style={{ animationDelay: `${index * 0.03}s` }}
                  className={`ez-3d-option-card py-2.5 px-3 flex items-center justify-between ${
                    parseFloat(amountInput) === opt.val ? 'is-selected' : ''
                  }`}
                >
                  <div className="flex flex-col items-start text-left">
                    <span className="font-bold text-sm tracking-wide text-white">{opt.label}</span>
                    <span className="text-[10px] text-slate-400">Preset Stake</span>
                  </div>
                  {parseFloat(amountInput) === opt.val ? (
                    <div className="w-5 h-5 rounded-full bg-[#fc4420] flex items-center justify-center text-white shadow-[0_0_8px_rgba(252,68,32,0.6)]">
                      <Check size={12} strokeWidth={3} />
                    </div>
                  ) : (
                    <div className="w-4 h-4 rounded-full border border-white/20" />
                  )}
                </button>
              ))}
            </div>

            {/* Confirm button */}
            <div className="pt-3 mt-1">
              <button
                onClick={() => closeAmountMenu()}
                className="ez-3d-confirm-btn"
              >
                <span>Confirm Stake</span>
                <span className="bg-black/25 px-2 py-0.5 rounded-md text-xs font-bold text-white tracking-wide">
                  ${parsedAmount.toFixed(2)}
                </span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
